<template>
  <ScrollTop v-if="showScrollTop" />
  <div class="englobe-params">
    <div class="params-row top">
      <FiltersMenu
        :active-filters="activeFilters"
        :is-loading="store.loading"
        :disabled="!store.filters"
        @remove-filter="removeFilterItem"
        @reset-all-filters="resetAllFilters"
      >
        <SearchAsYouTypeFilter
          v-if="!store.isFollawedActive"
          filter="companies"
          :items="store.companies"
          item-label="name"
          item-value="national_id"
          :active-filters="selectedFilters.companies"
          :is-loading="store.isCompanyFilterLoading"
          @search="fetchCompanies"
          @add-filter-item="addFilterItem"
          @remove-filter-item="removeFilterItem"
          @reset-filter="resetCompanyFilter"
        >
          Organisations
        </SearchAsYouTypeFilter>
        <SelectMultipleCounter
          v-for="(value, key) in filtersNames"
          :key="key"
          :filter="key"
          :items="store.filters[key] ?? []"
          :active-filters="selectedFilters[key]"
          :has-search="store.filters[key]?.length > 10"
          :disabled="!store.filters[key] || !store.filters[key].length"
          @add-filter-item="addFilterItem"
          @remove-filter-item="removeFilterItem"
          @reset-filter="resetFilter"
        >
          {{ value }}
        </SelectMultipleCounter>
      </FiltersMenu>

      <FollawedRadioButtons
        all-match-route="/lobbies"
        follawed-route="/lobbies/follawed"
      />
    </div>

    <div v-if="store.isFollawedActive && store.items.length" class="params-row">
      <RouterRadioButtons :options="textOptions" />
    </div>

    <LobbiesNewsParams v-if="route.fullPath.includes('news')" />

    <div v-else-if="!route.fullPath.includes('activities')" class="params-row">
      <TotalMatched :item-count="store.itemCount" />
      <div class="page-settings">
        <DropdownMenu
          v-model="selectedSorterOption"
          :options="sortersOptions"
          label="Trier par :"
        />

        <DropdownMenu
          v-model="selectedLimitOption"
          :options="limitOptions"
          label="Lobbies par page :"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { storeToRefs } from "pinia";
import { useRoute } from "vue-router";

import ScrollTop from "@follaw-fr/components/reusable/atoms/ScrollTop.vue";
import FiltersMenu from "@follaw-fr/components/filter-menu/organisms/FiltersMenu.vue";
import SelectMultipleCounter from "@follaw-fr/components/filter-menu/molecules/SelectMultipleCounter.vue";
import SearchAsYouTypeFilter from "@follaw-fr/components/filter-menu/molecules/SearchAsYouTypeFilter.vue";
import RouterRadioButtons from "@follaw-fr/components/reusable/molecules/RouterRadioButtons.vue";
import LobbiesNewsParams from "./LobbiesNewsParams.vue";
import TotalMatched from "@follaw-fr/components/reusable/atoms/TotalMatched.vue";
import side_menu from "@follaw-fr/utils/side_menu";
import { useLobbiesStore } from "@follaw-fr/stores/lobbies.js";
import FollawedRadioButtons from "@/follaw-fr/components/reusable/atoms/FollawedRouterRadioButtons.vue";
import { useDropdown } from "@follaw-fr/composables/useDropdown";
import DropdownMenu from "@follaw-fr/components/reusable/atoms/DropdownMenu.vue";

const store = useLobbiesStore();
const route = useRoute();
const { selectedFilters } = storeToRefs(store);

const fetchLobbiesList = async () => {
  selectedFilters.value.from = 0;
  await store.readLobbies();
};

const fetchCompanies = (event) => {
  if (event.length > 0) {
    store.getCompanyFilters(event);
  } else {
    store.companies = [];
  }
};

const { options: limitOptions, selectedValue: selectedLimitOption } =
  useDropdown({
    options: { 10: "10", 20: "20", 50: "50", 200: "200" },
    selectedFilters,
    filterKey: "size",
    onChange: () => {
      fetchLobbiesList();
    },
  });

const { options: sortersOptions, selectedValue: selectedSorterOption } =
  useDropdown({
    options: {
      expenses: "Dépenses",
      money: "CA",
      number_of_employees: "Employés",
      number_of_publications: "Publications",
    },
    selectedFilters,
    filterKey: "sort_field", // Matches a key in initialLobbiesFilters
    onChange: fetchLobbiesList,
  });

// All filters in wanted display order with their labels
const filtersNames = {
  type: "Type d'organisation",
  city: "Localité",
  levels: "Niveau",
  categories: "Secteur",
};

const showScrollTop = computed(() => selectedFilters.value.size > 20);

// Build follawed navbar options from router config
const routerOptions = side_menu
  .find((r) => r.name === "PRM")
  .secondary_tabs.find((tab) => tab.name === "Lobbies").children;
const textOptions = routerOptions.map((r) => ({
  label: r.name.replace("Lobbies Follawed - ", ""),
  value: `/lobbies/${r.path}`,
  exact: r.name === "Lobbies Follawed - Leurs activités" ? false : true,
}));

const addFilterItem = (event) => {
  const targetFilters = selectedFilters.value[event.filter] || [];

  selectedFilters.value = {
    ...selectedFilters.value,
    [event.filter]: [...targetFilters, event.item],
  };
  fetchLobbiesList();
};

const removeFilterItem = (event) => {
  let targetFilters = selectedFilters.value[event.filter] || [];

  targetFilters = targetFilters.filter((el) => el !== event.item);

  if (targetFilters.length === 0) {
    delete selectedFilters.value[event.filter];
  } else {
    selectedFilters.value = {
      ...selectedFilters.value,
      [event.filter]: targetFilters,
    };
  }
  fetchLobbiesList();
};

const activeFilters = computed(() => {
  let arr = [];
  // Put all selected options from all filters with said filter name in an array for the SelectedFilters component
  Object.keys(filtersNames).forEach((key) => {
    selectedFilters.value[key]?.forEach((filter) => {
      arr = [...arr, { filter: key, item: filter }];
    });
  });
  selectedFilters.value.companies?.forEach((filter) => {
    arr = [
      ...arr,
      {
        filter: "companies",
        item: filter,
      },
    ];
  });
  return arr;
});

const resetCompanyFilter = () => {
  store.companies = [];
  delete selectedFilters.value.companies;
  fetchLobbiesList();
};

const resetFilter = (event) => {
  delete selectedFilters.value[event];
  fetchLobbiesList();
};

const resetAllFilters = () => {
  store.resetFilters();
  fetchLobbiesList();
};
</script>

<style lang="scss" scoped>
.params-wrapper {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 10px;
}

.legislature {
  margin: 0;
}
</style>
